<script>
import { range, startCase } from 'lodash'

const DEFAULT_PER_PAGE = 50

export default {
  name: 'EaPagination',
  emits: ['update:page', 'update:perPage'],
  props: {
    disablePagination: { type: Boolean, default: false },
    total: { type: Number },
    perPage: { type: Number },
    page: { type: Number },
    perPageOptions: { type: Array, default: () => [20, 30, 50, 100, 250, 500, 1000] },
    recordsName: { type: String, default: 'record(s)' },
  },
  computed: {
    perPageDropdownActions() {
      return this.perPageOptions.map(opt => {
        return {
          title: startCase(opt),
          clickFn: (e, closeFn) => this.perPageChanged(e, closeFn, opt),
        }
      })
    },
    lastPage() {
      const value = Math.ceil(((this.total || 0) * 1.0) / (this.perPage || DEFAULT_PER_PAGE))
      if (this.total && (this.page || 1) > value) {
        this.$emit('update:page', this.lastPage)
      }
      return value
    },
    displayedPages() {
      let min = this.page - 2
      if (min < 1) min = 1
      let max = this.page + 2
      if (max > this.lastPage) max = this.lastPage
      return range(min, max + 1)
    },
  },
  mounted() {
    if (!this.perPage || this.perPageOptions.indexOf(this.perPage) == -1) {
      this.$emit('update:perPage', DEFAULT_PER_PAGE)
    }
    if (!this.page || this.page < 1) {
      this.$emit('update:page', 1)
    }
  },
  methods: {
    perPageChanged(evt, closeFn, value) {
      evt.preventDefault()
      if (closeFn) closeFn()
      value == 'all' ? this.$emit('update:perPage', 100000) : this.$emit('update:perPage', value)
      this.$emit('update:page', 1)
    },
    pageClicked(evt, pageNo) {
      evt.preventDefault()
      if (pageNo != this.page) {
        this.$emit('update:page', pageNo)
      }
    },
  },
}
</script>

<template lang="pug">
.pagination-container.d-flex.align-items-center.justify-content-between.flex-column.flex-sm-row(
  v-if="page && total && !disablePagination"
)
  .pagination-container-text.small
    span(v-if="lastPage > 1") Displaying {{ (page - 1) * perPage + 1 }} - {{ page * perPage > total ? total : page * perPage }} of total {{ total }} {{ recordsName }}.
    span(v-else) Displaying {{ total }} {{ recordsName }}.

  nav.d-flex.flex-column.flex-sm-row.align-items-center(aria-label="Pagination")
    ul.pagination.pagination-sm(v-if="lastPage > 1")
      li.page-item(:class="{ disabled: page == 1 }")
        a.page-link(@click="pageClicked($event, 1)") First
      li.page-item(:class="{ disabled: page == 1 }")
        a.page-link(@click="pageClicked($event, page - 1)")
          i.bi-chevron-left
      li.page-item(
        v-for="pageNo in displayedPages"
        :class="{ active: pageNo == page }"
        :key="pageNo"
      )
        a.page-link(@click="pageClicked($event, pageNo)") {{ pageNo }}
      li.page-item(:class="{ disabled: page == lastPage }")
        a.page-link(@click="pageClicked($event, page + 1)")
          i.bi-chevron-right
      li.page-item(:class="{ disabled: page == lastPage }")
        a.page-link(@click="pageClicked($event, lastPage)") Last

    dropdown.ms-3(:actions="perPageDropdownActions" name="perPage")
      button.btn.btn-md.with-icon.btn-outline-primary.btn-sm.dropdown-toggle {{ perPage === 100000 ? 'all records' : `${perPage}` }}
</template>

<style scoped lang="scss">
@use 'sass:map';
@use 'frontend_stylesheets/theme-variables.scss' as themeVariables;

.pagination-container {
  z-index: 2;
}
.page-item {
  .page-link {
    border-color: transparent;
    border-radius: 4px;
    color: map.get(themeVariables.$colors, primary) !important;
    background: transparent;
  }

  &.active .page-link {
    background: map.get(themeVariables.$colors, secondaryContainer) !important;
    border-color: transparent !important;
    color: map.get(themeVariables.$colors, onSecondaryContainer) !important;
  }
}
</style>
